import { useState } from "react";
import { useSearchParams, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Shield, Clock, Users, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Reveal } from "@/components/Reveal";
import LeadForm from "@/components/LeadForm";
import { DuckBadge } from "@/components/DuckMascot";
import { PESTS, getPest } from "@/data/pests";

// Maps a pest slug to the exact label used in the lead form's PEST_OPTIONS,
// so a pest-specific landing page preselects step 1 correctly.
const SLUG_TO_OPTION = {
  mosquitoes: "Mosquitoes",
  ticks: "Ticks",
  "wasps-hornets": "Wasps/Hornets",
  ants: "Ants",
  fleas: "Fleas",
  rodents: "Rodents",
};

const EXPECT = [
  { icon: Shield, title: "Problem first, then details", body: "We ask what's bugging you before anything personal. Takes about a minute." },
  { icon: Users, title: "Matched to a local provider", body: "We help connect you with an appropriate independent pro in your area." },
  { icon: Clock, title: "No obligation", body: "Submitting a request doesn't commit you to anything. You stay in control." },
];

function ThankYou({ data }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      className="rounded-[2rem] border border-forest/10 bg-cream shadow-xl shadow-forest/5 p-8 sm:p-14 text-center"
      data-testid="lead-thank-you"
    >
      <div className="flex justify-center mb-6">
        <div className="relative">
          <DuckBadge size={88} />
          <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-duckgreen flex items-center justify-center ring-4 ring-cream">
            <CheckCircle2 className="w-5 h-5 text-forest-950" />
          </div>
        </div>
      </div>
      <h2 className="font-serif text-3xl sm:text-4xl font-bold text-forest-950">Got it.</h2>
      <p className="mt-4 text-lg text-forest-950/75 max-w-lg mx-auto leading-relaxed">
        We'll review what's bugging you{data?.pest ? <> — <span className="font-semibold text-forest">{data.pest}</span></> : ""} and help point you in the right direction. Keep an eye on your {data?.preferred_contact?.toLowerCase() || "inbox"}.
      </p>
      <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
        <Link to="/guides/things-to-check-before-hiring" className="inline-flex items-center justify-center px-7 py-3.5 rounded-full bg-forest-950 text-cream font-semibold hover:bg-forest transition-all" data-testid="thankyou-vet-btn">
          7 things to check before you hire
        </Link>
        <Link to="/guides" className="inline-flex items-center justify-center px-7 py-3.5 rounded-full border border-forest/20 text-forest-950 font-semibold hover:bg-pill/60 transition-all" data-testid="thankyou-guides-btn">
          Read the guides
        </Link>
      </div>
    </motion.div>
  );
}

// Lets someone on the generic page jump to a pest-specific journey.
function PestPicker({ activeSlug }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid="hire-pest-picker">
      {PESTS.map((p) => {
        const active = p.slug === activeSlug;
        return (
          <Link
            key={p.slug}
            to={`/hire-a-pro/${p.slug}`}
            className={`px-4 py-2.5 rounded-full text-sm font-medium border transition-all ${
              active
                ? "bg-duckgreen text-forest-950 border-duckgreen"
                : "bg-card text-forest-950/75 border-forest/15 hover:border-forest/35"
            }`}
            data-testid={`hire-pest-chip-${p.slug}`}
          >
            {p.name}
          </Link>
        );
      })}
    </div>
  );
}

export default function HireAPro() {
  const { pestSlug } = useParams();
  const [params] = useSearchParams();
  const [submitted, setSubmitted] = useState(null);

  const pest = pestSlug ? getPest(pestSlug) : null;
  // Slug route wins; the legacy ?pest= query string still works for old links.
  const presetPest = pest ? SLUG_TO_OPTION[pest.slug] : params.get("pest") || "";

  const heading = pest
    ? `Dealing with ${pest.name.toLowerCase()}? Find a Connecticut pro.`
    : "Find a pest control pro in Connecticut.";

  const subhead = pest
    ? `Tell us what's going on and we'll help connect you with a local provider who handles ${pest.name.toLowerCase()}. Takes about a minute, no obligation.`
    : "Tell us what's bugging you and we'll help connect you with an appropriate local provider. Takes about a minute, no obligation.";

  const seoTitle = pest ? `${pest.name} Control in Connecticut — Find a Pro` : "Find a Pest Control Pro in Connecticut";
  const seoDesc = pest
    ? `Dealing with ${pest.name.toLowerCase()} in Connecticut? Tell us what's going on and we'll help connect you with an appropriate local pest-control provider. No obligation.`
    : "Tell us what's bugging you and where you live. DuckTheBugs helps connect Connecticut homeowners with an appropriate local pest-control provider. No obligation.";

  return (
    <div data-testid="hire-a-pro-page">
      <Seo title={seoTitle} description={seoDesc} path={pest ? `/hire-a-pro/${pest.slug}` : "/hire-a-pro"} />

      {/* Deliberately compact. Someone who clicked "Hire a Pro" has already
          decided — this hero only has to confirm they're in the right place,
          not sell them again. Everything here is sized so the form's first
          field lands inside the opening screen on a phone. */}
      <section className="bg-forest-950 text-cream grain pt-[72px]">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-8 sm:py-12 md:py-16">
          <Breadcrumbs
            items={
              pest
                ? [{ label: "Home", to: "/" }, { label: "Hire a Pro", to: "/hire-a-pro" }, { label: pest.name }]
                : [{ label: "Home", to: "/" }, { label: "Hire a Pro" }]
            }
          />
          <h1 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl font-black leading-[1.05] max-w-3xl">
            {heading}
          </h1>
          <p className="mt-4 text-base sm:text-lg text-cream/80 max-w-2xl leading-relaxed">{subhead}</p>
          <p className="mt-3 text-xs text-cream/55 max-w-2xl leading-relaxed">
            DuckTheBugs is an independent resource and matching service — we don't perform treatments ourselves.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-12 py-8 sm:py-12 md:py-16">
        <div className="grid lg:grid-cols-[1fr_1.3fr] gap-10 lg:gap-14 items-start">
          {/* The form is FIRST in the DOM so a phone reaches it with no
              scrolling. On desktop it moves to the right column, where both
              it and the supporting copy sit above the fold anyway. */}
          <Reveal className="lg:order-2">
            {submitted ? <ThankYou data={submitted} /> : <LeadForm presetPest={presetPest} onSuccess={setSubmitted} />}
          </Reveal>

          <Reveal className="lg:order-1" delay={0.1}>
            <div className="lg:sticky lg:top-28 space-y-6">
              <div>
                <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-terracotta">What to expect</span>
                <h2 className="mt-3 font-serif text-2xl sm:text-3xl font-bold text-forest-950">
                  {pest ? `What a pro typically does about ${pest.name.toLowerCase()}` : "What happens after you submit"}
                </h2>
              </div>

              {pest ? (
                <ul className="space-y-3" data-testid="hire-pro-treatment">
                  {pest.proTreatment.map((item) => (
                    <li key={item} className="flex gap-3">
                      <CheckCircle2 className="w-5 h-5 text-forest shrink-0 mt-0.5" />
                      <span className="text-sm text-forest-950/75 leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                EXPECT.map((e) => (
                  <div key={e.title} className="flex gap-4">
                    <div className="w-11 h-11 rounded-2xl bg-duckgreen/12 flex items-center justify-center shrink-0">
                      <e.icon className="w-5 h-5 text-forest" />
                    </div>
                    <div>
                      <h3 className="font-serif text-lg font-bold text-forest-950">{e.title}</h3>
                      <p className="mt-1 text-sm text-forest-950/70 leading-relaxed">{e.body}</p>
                    </div>
                  </div>
                ))
              )}

              <div className="rounded-2xl bg-teal/5 border border-teal/20 p-5">
                <p className="text-sm text-teal leading-relaxed">
                  <span className="font-semibold">Kids &amp; pets:</span> when you talk to a provider, ask what product they use, where they'll apply it, and how long before kids and pets can return. That re-entry time comes from the product label.
                </p>
              </div>

              {/* Moved out of the hero on purpose: a link away from the page,
                  sitting above the form, is a leak on a conversion page. */}
              {pest && (
                <Link
                  to={`/pests/${pest.slug}`}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-forest hover:text-terracotta transition-colors"
                  data-testid="hire-back-to-pest"
                >
                  Read the {pest.name.toLowerCase()} field guide <ArrowRight className="w-4 h-4" />
                </Link>
              )}

              <div className="pt-2">
                <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-forest-950/50">
                  {pest ? "Different pest?" : "Jump to your pest"}
                </span>
                <div className="mt-3">
                  <PestPicker activeSlug={pest?.slug} />
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
